import { FormField, Heading, RadioChipGroup, RadioChipOptions, Select, Text } from '@expressable/syntax-ds';
import { Clock } from '@phosphor-icons/react';
import { captureException } from '@sentry/react';
import { format } from 'date-fns';
import { SlotAvailability } from 'domains/calendars/v3/useEvaluationCalendarSummary';
import { UseEvaluationCalendarResult } from 'domains/signupDirect/misc/useSignupEvaluationCalendar';
import { EvaluationScheduleFormTypeV2 } from 'domains/signupDirect/schemas/evaluationScheduleSchemaV2';
import { TimezoneOption, timeZoneOptions } from 'domains/timezone';
import { useDisplayTimezone } from 'domains/timezone/useDisplayTimezone';
import { isEmpty } from 'lodash';
import { FC, useEffect, useMemo, useState } from 'react';
import { useController, UseControllerReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DAY_MONTH_YEAR, MONTH_DAY_SHORT, TIME_SHORT, WEEK_DAY_LONG, WEEK_DAY_SHORT } from 'utils/date';
import { SignupError } from 'utils/errors/debuggableError';

type Props = {
  onChange?: (timeSlot?: SlotAvailability) => void;
  name: string;
  calendarData: Extract<UseEvaluationCalendarResult, { isLoading: false }>;
};

// For signup pick first time slot v2 (pick by time slot) ***ONLY***

export const FirstSplitTimeSlotPickerV2: FC<Props> = ({ onChange, name, calendarData }) => {
  // @TODO(juan): types need to be cleaned up here, and properly separate the component from RHF schema
  const {
    field,
    fieldState: { error },
  } = useController({ name: name as 'timeSlot' }) as UseControllerReturn<
    Partial<EvaluationScheduleFormTypeV2>,
    'timeSlot'
  >;

  const { currentAvailability, dayIndex, onChangeDay } = calendarData;

  const { day: currentDay, availabilityBySlot } = currentAvailability;

  // Define if there are no available slots (depends on the timelineSlots and if all requests are resolved)
  const noAvailableSlots = availabilityBySlot.length === 0;

  const { displayTimezone, setDisplayTimezone, format: formatInTimezone } = useDisplayTimezone();

  const dayOptions: RadioChipOptions = useMemo(() => {
    return calendarData.allTimeSlots.map(({ day, availabilityBySlot }, index) => {
      const hasSlots = availabilityBySlot.length > 0;
      return {
        value: `${index}`,
        label: (<DayPickerChip day={day} availabilityBySlot={availabilityBySlot} />) as unknown as string, //@TODO make syntax allow React.Node
        disabled: !hasSlots,
      };
    });
  }, [calendarData]);

  const onChangeTimeSlot = (slot?: SlotAvailability) => {
    onChange?.(slot);
    field.onChange(slot);
  };

  const { t } = useTranslation();

  const GhostAnchor = () => <div ref={field.ref} className="hidden" />;

  // Start day carousel on first day with available slots
  const [startIndex, setStartIndex] = useState<number | undefined>();

  useEffect(() => {
    const dayOptionIndex = dayOptions.findIndex(option => !option.disabled);

    if (dayOptionIndex !== -1 && dayIndex < dayOptionIndex) {
      onChangeDay(dayOptionIndex);
      setStartIndex(dayOptionIndex);
    }
  }, [dayOptions, dayIndex, onChangeDay]);

  return (
    <>
      <FormField label={t('directSignup.schedule.timezone.label')} name={!displayTimezone ? name : undefined}>
        <Select
          options={timeZoneOptions}
          onChange={selectedValue => {
            if (isEmpty(selectedValue)) {
              onChangeTimeSlot(undefined);
              setDisplayTimezone(undefined);
            } else {
              setDisplayTimezone(timeZoneOptions.find(({ value }) => value === selectedValue) as TimezoneOption);
            }
          }}
          value={displayTimezone?.value}
          standalone
        />
      </FormField>

      <FormField
        name={noAvailableSlots ? name : undefined}
        label={t('directSignup.schedule.split.second.dayPicker.label')}
        config={{ order: { error: 'top', hint: 'top' } }}
      >
        <RadioChipGroup
          htmlName="daypicker"
          chips={dayOptions}
          variant="carousel"
          value={`${dayIndex}`}
          startIndex={startIndex}
          onChange={e => {
            onChangeTimeSlot(undefined);
            onChangeDay(parseInt(e.target.value, 10));
          }}
          standalone
        />
      </FormField>

      {noAvailableSlots && (
        <Text className="text-center" variant="h6">
          {t('directSignup.schedule.noSlots')} <GhostAnchor />
        </Text>
      )}
      <Heading level="h4" noDefaultMargins>
        {t('directSignup.schedule.split.second.currentDayTitle', {
          day: format(currentDay, WEEK_DAY_LONG),
          date: format(currentDay, DAY_MONTH_YEAR),
        })}
      </Heading>

      {availabilityBySlot.length > 0 && (
        <FormField
          name={name}
          label={t('directSignup.schedule.split.second.timePicker.label')}
          config={{
            order: { error: 'bottom', hint: 'top' },
            labelSpacing: 'lg',
          }}
          // Note(juan): placeholder manual styles and build while we experiment with this component, will later be ported to syntax
        >
          <RadioChipGroup
            htmlName={`timeslotPicker}`}
            chips={availabilityBySlot.map(slot => ({
              value: slot.id,
              label: formatInTimezone(slot.time, TIME_SHORT),
            }))}
            variant="standard"
            onChange={e => {
              const pickedId = e.target.value;
              const slot = availabilityBySlot.find(s => s.id === pickedId);
              if (!slot) {
                captureException(new SignupError(`Unable to find slot with id: ${pickedId}`, {
                  debugData: {
                    pickedId,
                    therapistAvailability: JSON.stringify(availabilityBySlot),
                  },
                }));
              }

              onChangeTimeSlot(slot);
            }}
            value={field.value?.id ?? ''}
            standalone
            error={error}
            className="[&>*]:mobile-xs-d:grid-cols-2" //@TODO will be moved to syntax
          />
        </FormField>
      )}

      <GhostAnchor />
    </>
  );
};

const DayPickerChip = ({ day, availabilityBySlot }: { day: Date; availabilityBySlot: SlotAvailability[] }) => {
  const { format: formatInTimezone } = useDisplayTimezone();
  return (
    <div className="flex flex-col gap-e2 items-center whitespace-pre-line w-full">
      <Text variant="label">{formatInTimezone(day, WEEK_DAY_SHORT)}</Text>
      <Text variant="body-md">{formatInTimezone(day, MONTH_DAY_SHORT)}</Text>
      <Text variant="body-xs" className="flex items-center gap-e2">
        {availabilityBySlot.length} <Clock weight="fill" className="size-e14 text-purple-500" />
      </Text>
    </div>
  );
};
